import { Breakpoint, createTheme, ThemeOptions } from '@mui/material';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/700.css';

/*
 * // https://mui.com/material-ui/customization/palette
 * primary
 * - main color of the app, for the header background, etc.
 * - brand color
 * - @todo then why does mui used primary.main for input border color and checkbox background?
 *
 * secondary
 * - accent and distinguish
 * - action buttons, selection controls
 * - links, progress bars
 */

declare module '@mui/material/styles' {
  interface TypeBackground {
    // Add custom background colors
    default: string;
    paper: string;
    sidebar: string;
    linkbar: string;
    traybar: string;
  }

  interface BreakpointOverrides {
    xxs: true;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    surface: {
      primary: {
        main: string;
      };
      secondary: {
        main: string;
      };
      info: {
        main: string;
      };
      error: {
        main: string;
      };
      warning: {
        main: string;
      };
      success: {
        main: string;
      };
    };
    link: {
      main: string;
    };
  }

  interface PaletteOptions {
    surface: {
      primary: {
        main: string;
      };
      secondary: {
        main: string;
      };
      info: {
        main: string;
      };
      error: {
        main: string;
      };
      warning: {
        main: string;
      };
      success: {
        main: string;
      };
    };
    link: {
      main: string;
    };
  }
}

/*
 * WARNING: If you change theme values below, you should check the index.html file, as there are a few
 * important CSS values copied into style tags in that file. The reason for this is to load the user's
 * theme preference (i.e. light|dark) as quickly as possible, not having to wait for react to boot. This
 * helps to prevent a brief FOUC when the page is first loading.
 */

export const LightTheme: ThemeOptions = {
  palette: {
    mode: 'light',
    common: {
      black: '#3c4043',
      white: '#ffffff',
    },
    background: {
      default: '#f0f4f9',
      paper: '#ffffff',
      sidebar: '#dfe5ef',
      linkbar: '#e8eef5',
      traybar: '#f4f7fb',
    },
    text: {
      primary: '#3c4043eb',
      secondary: '#5f63689e',
      disabled: '#5f636861',
    },
    divider: '#dadce0',
    surface: {
      primary: {
        main: '#00263e',
      },
      secondary: {
        main: '#6085c4',
      },
      info: {
        main: '#06aed41f',
      },
      error: {
        main: '#f044381f',
      },
      warning: {
        main: '#f5cb9654',
      },
      success: {
        main: '#12d4061f',
      },
    },
    primary: {
      main: '#4b66da',
    },
    secondary: {
      main: '#00436d',
    },
    link: {
      main: '#0086db',
    },
    success: {
      main: '#659d68',
    },
    error: {
      main: '#ea554e',
    },
  },
  typography: {
    fontFamily: 'Inter, Arial',
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 450,
    fontWeightBold: 500,
  },
  shape: {
    borderRadius: 5,
  },
  breakpoints: {
    values: {
      xxs: 0,
      xs: 420,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
};

export const DarkTheme: ThemeOptions = {
  ...LightTheme,
  palette: {
    ...LightTheme.palette!,
    mode: 'dark',
    common: {
      black: '#1b1d20',
      white: '#ffffffde',
    },
    background: {
      default: '#1d1d1e', // WARNING should match index.html declaration
      sidebar: '#343434',
      linkbar: '#181819',
      traybar: '#1b1b1b',
    },
    text: {
      primary: '#ffffffde', // WARNING should match index.html declaration
      secondary: '#ffffffb3',
      disabled: '#ffffff80',
    },
    divider: '#2b2b2b',
    action: {
      hover: '#343434',
    },
  },
};

// this is purely for the deps of the getBreakpoint function. breakpoint should be the same across themes.
const Theme = createTheme(LightTheme);

export const getBreakpoint = (size: Breakpoint) => Theme.breakpoints.down(size);
